import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import { LandingLanguageProvider, useLandingLanguage } from '../context/LandingLanguageContext';
import { aboutCopy } from '../i18n/aboutCopy';
import useIsMobile from '../hooks/useIsMobile';

const gold = '#D4AF37';
const text = '#F5F1EA';
const muted = 'rgba(245,241,234,0.72)';

function BulletList({ items }) {
  return (
    <ul
      style={{
        margin: '0 0 28px',
        padding: 0,
        listStyle: 'none',
      }}
    >
      {items.map((item) => (
        <li
          key={item}
          style={{
            position: 'relative',
            paddingLeft: '22px',
            marginBottom: '12px',
            color: muted,
            fontSize: 'clamp(15px, 1.2vw, 17px)',
            lineHeight: 1.55,
          }}
        >
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: '0.55em',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: gold,
              boxShadow: '0 0 8px rgba(212,175,55,0.45)',
            }}
            aria-hidden
          />
          {item}
        </li>
      ))}
    </ul>
  );
}

function SectionTitle({ children }) {
  return (
    <h2
      style={{
        margin: '0 0 16px',
        fontSize: 'clamp(20px, 2.2vw, 26px)',
        fontWeight: '800',
        letterSpacing: '-0.4px',
        color: gold,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {children}
    </h2>
  );
}

function AboutContent() {
  const { locale, t } = useLandingLanguage();
  const isMobile = useIsMobile();
  const c = aboutCopy[locale] || aboutCopy.en;

  useEffect(() => {
    document.title = `${c.pageTitle} · SIMO Pay`;
    return () => {
      document.title = 'SIMO Pay';
    };
  }, [c.pageTitle]);

  const pStyle = {
    margin: '0 0 22px',
    color: muted,
    fontSize: 'clamp(15px, 1.25vw, 18px)',
    lineHeight: 1.7,
    maxWidth: '720px',
  };

  return (
    <main
      style={{
        /* Mobile nav: logo row + full-width waitlist row — must clear fixed header */
        paddingTop: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 148px)' : '96px',
        paddingBottom: '64px',
        paddingLeft: isMobile ? '20px' : '48px',
        paddingRight: isMobile ? '20px' : '48px',
        maxWidth: '820px',
        margin: '0 auto',
      }}
    >
      <Link
        to="/"
        style={{
          display: 'inline-block',
          marginBottom: '28px',
          color: 'rgba(212,175,55,0.85)',
          fontSize: '14px',
          fontWeight: '600',
          textDecoration: 'none',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {t('nav.backHome')}
      </Link>

      <h1
        style={{
          margin: '0 0 8px',
          fontSize: 'clamp(32px, 4vw, 44px)',
          fontWeight: '900',
          letterSpacing: '-1.2px',
          color: text,
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {c.headline}
      </h1>
      <p
        style={{
          margin: '0 0 36px',
          fontSize: 'clamp(16px, 1.4vw, 19px)',
          fontWeight: '700',
          color: gold,
          letterSpacing: '-0.2px',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {c.tagline}
      </p>

      <p style={pStyle}>{c.intro1}</p>
      <p style={pStyle}>{c.intro2}</p>

      <p style={{ ...pStyle, marginBottom: '12px' }}>{c.ecosystemLead}</p>
      <BulletList items={c.ecosystemBullets} />

      <p style={{ ...pStyle, marginBottom: '12px' }}>{c.expertiseLead}</p>
      <BulletList items={c.expertiseBullets} />

      <p style={pStyle}>{c.leadership}</p>

      <SectionTitle>{c.whatWeDoTitle}</SectionTitle>
      <p style={{ ...pStyle, marginBottom: '12px' }}>{c.whatWeDoLead}</p>
      <BulletList items={c.whatWeDoBullets} />

      <p style={pStyle}>{c.mobility}</p>
      <p style={pStyle}>{c.kiosks}</p>

      <SectionTitle>{c.visionTitle}</SectionTitle>
      <p style={pStyle}>{c.vision1}</p>
      <p style={{ ...pStyle, marginBottom: '12px' }}>{c.visionLead}</p>
      <BulletList items={c.visionBullets} />

      <p
        style={{
          ...pStyle,
          marginTop: '36px',
          marginBottom: '28px',
          fontSize: 'clamp(17px, 1.5vw, 21px)',
          fontWeight: '700',
          textAlign: 'center',
          color: text,
          lineHeight: 1.45,
          maxWidth: '100%',
          background: 'linear-gradient(135deg, #D4AF37 0%, #F0D070 45%, #D4AF37 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {c.mantra}
      </p>

      <p style={pStyle}>{c.closing1}</p>
      <p style={{ ...pStyle, fontWeight: '600', color: text }}>{c.closing2}</p>
      <p style={{ ...pStyle, marginBottom: 0, fontWeight: '600', color: text }}>{c.closing3}</p>
    </main>
  );
}

function AboutUsInner() {
  return (
    <div
      style={{
        background: '#0F0F0F',
        minHeight: '100vh',
        overflowX: 'clip',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <Navbar />
      <AboutContent />
      <Footer />
    </div>
  );
}

const AboutUsPage = () => (
  <LandingLanguageProvider>
    <AboutUsInner />
  </LandingLanguageProvider>
);

export default AboutUsPage;
